import React, { useEffect, useRef, useState } from 'react'

import { StyledTableCell, StyledTableRow } from '../../../components/EnchancedTable'
import { CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import EditIcon from '@mui/icons-material/Edit'
import { Tenant } from '../../../types'
import EnabledSwtich from '../../../components/EnabledSwtich'
import { SubscriptionPicker } from './SubscriptionPicker'

interface Props {
  tenant: Tenant
  saveEdit: (
    tenantId: string,
    name: string | null,
    internal: boolean | null,
    deprecated: boolean | null,
    subscribedFor: string[] | null
  ) => void
  loading: boolean
}

const TenantEditRow: React.FC<Props> = ({ tenant, saveEdit, loading }) => {
  const [edit, setEdit] = useState(false)
  const [name, setName] = useState(tenant.name)
  const [internal, setInternal] = useState(tenant.internal)
  const [deprecated, setDeprecated] = useState(tenant.deprecated)
  const [subscribedFor, setSubscribedFor] = useState(tenant.subscribedFor)
  const [copied, setCopied] = useState(false)
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
    }
  }, [])

  const handleCopyRefId = () => {
    navigator.clipboard.writeText(tenant.referenceId).then(() => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current)
      setCopied(true)
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000)
    }).catch(() => {})
  }

  const toggleEdit = () => {
    setEdit(!edit)
  }

  const onCancel = () => {
    setName(tenant.name)
    setInternal(tenant.internal)
    setDeprecated(tenant.deprecated)
    toggleEdit()
  }

  const onSave = () => {
    saveEdit(
      tenant.referenceId,
      tenant.name === name ? null : name,
      tenant.internal === internal ? null : internal,
      tenant.deprecated === deprecated ? null : deprecated,
      tenant.subscribedFor.join(',') === subscribedFor.join(',') ? null : subscribedFor
    )
    toggleEdit()
  }

  const setActive = (active: boolean) => setDeprecated(!active)

  const onChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  return edit ? (
    <StyledTableRow hover role="checkbox" tabIndex={-1} key={`edit_row_${tenant.referenceId}`}>
      <StyledTableCell padding="checkbox" align="center">
        <IconButton size="small" onClick={onSave} color="primary">
          {loading ? <CircularProgress size={24} sx={{ mr: 1, ml: 0.4, mb: 0.7, mt: 0.7 }} /> : <CheckIcon />}
        </IconButton>
        <IconButton size="small" onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </StyledTableCell>

      <StyledTableCell padding="checkbox" align="left"></StyledTableCell>

      <StyledTableCell align="left">
        <TextField value={name} onChange={onChangeName} />
      </StyledTableCell>

      <StyledTableCell align="left">
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'text.secondary' }}>
          {tenant.referenceId}
        </Typography>
      </StyledTableCell>

      <StyledTableCell align="right">
        {<EnabledSwtich enabled={internal} editable={true} onChange={setInternal} />}
      </StyledTableCell>
      <StyledTableCell align="right">
        {<EnabledSwtich enabled={!deprecated} editable={true} onChange={setActive} />}
      </StyledTableCell>
      <StyledTableCell>
        <SubscriptionPicker value={subscribedFor} onChange={setSubscribedFor} />
      </StyledTableCell>
    </StyledTableRow>
  ) : (
    <StyledTableRow hover role="checkbox" tabIndex={-1} key={`view_row_${tenant.referenceId}`}>
      <StyledTableCell padding="checkbox" align="left">
        <IconButton size="small" onClick={toggleEdit}>
          {loading ? <CircularProgress size={24} sx={{ mr: 1, ml: 0.4, mb: 0.7, mt: 0.7 }} /> : <EditIcon />}
        </IconButton>
      </StyledTableCell>
      <StyledTableCell padding="checkbox" align="left"></StyledTableCell>
      <StyledTableCell align="left">{tenant.name}</StyledTableCell>

      <StyledTableCell align="left">
        <Tooltip title={copied ? 'Copied!' : 'Copy reference ID'}>
          <IconButton size="small" onClick={handleCopyRefId} sx={{ gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
              {tenant.referenceId}
            </Typography>
            <ContentCopyIcon sx={{ fontSize: '0.9rem' }} />
          </IconButton>
        </Tooltip>
      </StyledTableCell>

      <StyledTableCell align="right">
        {<EnabledSwtich enabled={internal} editable={false} onChange={setInternal} />}
      </StyledTableCell>
      <StyledTableCell align="right">
        {<EnabledSwtich enabled={!deprecated} editable={false} onChange={setActive} />}
      </StyledTableCell>
      <StyledTableCell>
        <SubscriptionPicker disabled={true} value={subscribedFor} onChange={setSubscribedFor} />
      </StyledTableCell>
    </StyledTableRow>
  )
}

export default TenantEditRow
